import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { Button, Link, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import acceptTerms from "@rc/App/Handlers/TermsAndConditions/acceptTerms";
import getTermsDocument from "@rc/App/Handlers/TermsAndConditions/getTermsDocument";
import { xngCheckIsDisplayModal } from "@rc/App/Services/TermsAndConditions/termsAndConditions";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps { }

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let tc;
    if (location && location.search) {
        const parsedQuery = parseQueryString<{ tc: string }>(location.search);
        tc = parsedQuery.tc;
    }

    return {
        tc,
    };
};

const mapDispatchToProps = {
    acceptTerms,
    getTermsDocument,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface TermsAndConditionsStyles {
    modal?: ModalPresentationProps;
    wrapper?: InjectableCss;
    message1?: TypographyPresentationProps;
    message2?: TypographyPresentationProps;
    agreeBtn?: ButtonPresentationProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: LinkPresentationProps;
}

export const termsAndConditionsStyles: TermsAndConditionsStyles = {
    modal: {
        size: 600,
        cssOverrides: {
            modalContainer: css`
                align-items: center;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    message2: {
        css: css`
            margin: 20px 0;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },
    agreeBtn: {
        variant: "primary",
        sizeVariant: "large",
        css: css`
            margin-left: 10px;
        `,
    },
};

const TermsAndConditionsModal = ({ tc, acceptTerms, getTermsDocument }: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [termsUrl, setTermsUrl] = useState<undefined | string>();

    useEffect(() => {
        const checkUrl = async () => {
            if (tc === "show") {
                const result = await xngCheckIsDisplayModal();
                result && getTermsDocument({
                    onSuccess(result: any) {
                        setTermsUrl(result);
                        if (result.length > 0) {
                            setIsModalOpen(true);
                        }
                    },
                });
            }
        }

        checkUrl();
    }, []);

    const agreeTerms = () => {
        acceptTerms({
            termsUrl,
            onSuccess(result: any) {
                modalCloseHandler();
            },
        });
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
    };

    return (
        <Modal
            {...termsAndConditionsStyles.modal}
            headline={translate("View terms and conditions")}
            isOpen={isModalOpen}
            handleClose={modalCloseHandler}
            data-test-selector="modal_ProductSelectorVariantModal"
        >
            <StyledWrapper>
                <Typography as="p">
                    {siteMessage(
                        "Custom_Terms_And_Conditions_1",
                        "Click below to view Terms and Conditions. By clicking I agree, I understand that I am agreeing to the Terms and Conditions, which are a legally binding agreement that I am electronically signing.",
                    )}
                </Typography>

                <Typography as="p" {...termsAndConditionsStyles.message2}>
                    {siteMessage(
                        "Custom_Terms_And_Conditions_2",
                        `By clicking on "I agree" below, you also agree that`,
                    )}
                </Typography>

                <Link href={termsUrl} target="_blank">
                    {translate("Show terms and conditions")} {translate("(opens in new window)")}
                </Link>

                <StyledWrapper {...termsAndConditionsStyles.buttonsWrapper}>
                    <Link {...termsAndConditionsStyles.cancelButton} onClick={modalCloseHandler}>
                        {translate("Cancel")}
                    </Link>
                    <Button {...termsAndConditionsStyles.agreeBtn} onClick={agreeTerms}>
                        {translate("I agree")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(TermsAndConditionsModal);
